import { Box, Card, Flex, Grid, Group, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { DotPattern } from "$/components/dot-pattern";
import { Reveal } from "$/components/reveal";
import { Section } from "$/components/section";
import type { Feature } from "$/data/features";
import { features } from "$/data/features";

export function Features() {
  const [vendas, catalogo, balanco, backup, clientes] = features;

  return (
    <Section id="features" bg="green.0">
      <Stack gap="md" maw={720} mb={56}>
        <Text c="green.7" tt="uppercase" fw="bold" fs="14px">
          — Funcionalidades
        </Text>

        <Title
          order={2}
          fz={{ base: 32, md: 48 }}
          fw={800}
          lh={1.1}
          style={{ letterSpacing: "-0.03em" }}
        >
          Tudo o que você precisa em um único lugar.
        </Title>
        <Text c="gray.7" fz={{ base: 16, md: 18 }} lh={1.55} maw={620}>
          Vendas, estoque, balanço, clientes, fornecedores, backups num aplicativo só. Somos um
          sistema inteiro pensado pro MEI e para pequenas lojas.
        </Text>
      </Stack>

      <Grid gap="md">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Reveal delay={0} style={{ height: "100%" }}>
            <FeatureCard size="lg" pattern data={vendas}>
              <Flex
                mt="lg"
                gap={{ base: "xs", xs: "md" }}
                direction={{ base: "column", xs: "row" }}
                wrap="wrap"
              >
                <Group gap={6} wrap="nowrap">
                  <Kbd>CTRL + K</Kbd>
                  <Text fz="xs" c="gray.6">
                    buscar itens
                  </Text>
                </Group>
                <Group gap={6} wrap="nowrap">
                  <Kbd>CTRL + V</Kbd>
                  <Text fz="xs" c="gray.6">
                    nova venda
                  </Text>
                </Group>
                <Group gap={6} wrap="nowrap">
                  <Kbd>CTRL + D</Kbd>
                  <Text fz="xs" c="gray.6">
                    nova despesa
                  </Text>
                </Group>
              </Flex>
            </FeatureCard>
          </Reveal>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Reveal delay={80} style={{ height: "100%" }}>
            <FeatureCard data={catalogo} />
          </Reveal>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Reveal delay={120} style={{ height: "100%" }}>
            <FeatureCard data={balanco} />
          </Reveal>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Reveal delay={180} style={{ height: "100%" }}>
            <FeatureCard data={backup} />
          </Reveal>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Reveal delay={240} style={{ height: "100%" }}>
            <FeatureCard data={clientes} />
          </Reveal>
        </Grid.Col>
      </Grid>
    </Section>
  );
}

type FeatureCardProps = {
  data: Feature;
  size?: "md" | "lg";
  featured?: boolean;
  pattern?: boolean;
  children?: React.ReactNode;
};

function FeatureCard({
  data,
  size = "md",
  featured = false,
  pattern = false,
  children,
}: FeatureCardProps) {
  const { icon: Icon, title, description } = data;

  return (
    <Card
      padding={size === "lg" ? 32 : "xl"}
      radius="lg"
      withBorder
      className={`feature-card${featured ? " feature-card-featured" : ""}`}
      style={
        featured
          ? {
              height: "100%",
              border: "1px solid var(--mantine-color-green-7)",
              background:
                "linear-gradient(135deg, var(--mantine-color-green-7) 0%, var(--mantine-color-green-5) 100%)",
            }
          : { height: "100%", overflow: pattern ? "hidden" : undefined }
      }
    >
      {pattern && (
        <DotPattern
          id="feature-vendas-dots"
          color="var(--mantine-color-green-3)"
          mask="radial-gradient(ellipse 60% 70% at 100% 0%, black 0%, transparent 70%)"
          style={{ zIndex: 0 }}
        />
      )}
      <Box pos="relative" style={{ zIndex: 1 }}>
        {featured ? (
          <Box
            mb="md"
            className="feature-card-icon"
            style={{
              width: 56,
              height: 56,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "var(--mantine-radius-md)",
              border: "1.5px solid white",
              background: "rgba(255, 255, 255, 0.2)",
              color: "white",
            }}
          >
            <Icon size={28} />
          </Box>
        ) : (
          <ThemeIcon
            size={44}
            radius="md"
            variant="gradient"
            gradient={{ from: "green.7", to: "green.4", deg: 135 }}
            mb="md"
            className="feature-card-icon"
          >
            <Icon size={22} />
          </ThemeIcon>
        )}
        <Text
          fw={700}
          fz={size === "lg" ? { base: 22, md: 26 } : "lg"}
          mb="xs"
          lh={1.2}
          c={featured ? "white" : undefined}
        >
          {title}
        </Text>
        <Text
          c={featured ? "green.0" : "gray.7"}
          fz={size === "lg" ? { base: 15, md: 16 } : "sm"}
          lh={1.6}
        >
          {description}
        </Text>
        {children}
      </Box>
    </Card>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      px={8}
      py={2}
      bg="white"
      style={{
        borderRadius: 6,
        border: "1px solid var(--mantine-color-gray-3)",
        boxShadow: "0 1px 0 var(--mantine-color-gray-3)",
        fontFamily: "JetBrains Mono, monospace",
        fontSize: 12,
        fontWeight: 600,
        color: "var(--mantine-color-dark-9)",
      }}
    >
      {children}
    </Box>
  );
}
